import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePremium } from '../contexts/PremiumContext';

const FONT = 'Manrope, sans-serif';

const PLANS = [
  { id: 'dk.freshtrack.app.yearly',        labelKey: 'paywall.planSoloYearly',   price: '79 kr / år',  subKey: 'paywall.subSoloYearly',    best: true,  family: false },
  { id: 'dk.freshtrack.app.yearly.family', labelKey: 'paywall.planFamilyYearly', price: '149 kr / år', subKey: 'paywall.subFamilyYearly',  best: false, family: true },
  { id: 'dk.freshtrack.app.monthly',       labelKey: 'paywall.planSoloMonthly',  price: '19 kr / md',  subKey: null,                       best: false, family: false },
  { id: 'dk.freshtrack.app.monthly.family',labelKey: 'paywall.planFamilyMonthly',price: '35 kr / md',  subKey: 'paywall.subFamilyMonthly', best: false, family: true },
];

const FEATURES = [
  ['♾️', 'paywall.featUnlimited'],
  ['🏠', 'paywall.featExtraUnits'],
  ['👨‍👩‍👧', 'paywall.featHouseholds'],
  ['📷', 'paywall.featImages'],
  ['🔔', 'paywall.featPush'],
  ['🤖', 'paywall.featAutoAdd'],
];

export default function PaywallModal({ onClose, reason }) {
  const { t } = useTranslation();
  const { purchase, restore } = usePremium();
  const [selectedId, setSelectedId] = useState(PLANS[0].id);
  const [loading, setLoading]       = useState(null);
  const [error, setError]           = useState('');

  const handlePurchase = async () => {
    setLoading('buy');
    setError('');
    try {
      await purchase(selectedId);
      onClose();
    } catch (err) {
      if (err.message !== 'CANCELLED') {
        console.error('[IAP]', err.message, err);
        setError(t('paywall.purchaseFailed', { msg: err.message }));
      }
    } finally {
      setLoading(null);
    }
  };

  const handleRestore = async () => {
    setLoading('restore');
    setError('');
    try {
      const found = await restore();
      if (found) { onClose(); }
      else { setError(t('paywall.noPurchases')); }
    } catch (err) {
      console.error(err);
      setError(t('paywall.restoreFailed'));
    } finally {
      setLoading(null);
    }
  };

  const busy = !!loading;
  const selectedPlan = PLANS.find(p => p.id === selectedId);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'oklch(0% 0 0 / 70%)',
      zIndex: 50, display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      padding: 16,
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        background: 'var(--bg-elevated)',
        borderRadius: '24px 24px 20px 20px',
        overflow: 'hidden',
        boxShadow: 'var(--shadow-lg)',
        maxHeight: '92vh',
        overflowY: 'auto',
      }}>

        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, oklch(42% 0.16 175), oklch(30% 0.14 210))',
          padding: '28px 24px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>⭐</div>
          <h2 style={{ fontSize: 22, fontWeight: 800, color: '#fff', fontFamily: FONT, margin: 0 }}>
            {t('premium.upgrade')}
          </h2>
          {reason && (
            <p style={{ fontSize: 13, color: 'oklch(85% 0.06 175)', marginTop: 6, fontFamily: FONT }}>
              {reason}
            </p>
          )}
        </div>

        <div style={{ padding: '20px 20px 24px' }}>
          {/* Features */}
          <ul style={{ listStyle: 'none', margin: '0 0 20px', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {FEATURES.map(([icon, key]) => (
              <li key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--fg-2)', fontFamily: FONT }}>
                <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
                <span>{t(key)}</span>
              </li>
            ))}
          </ul>

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--border-subtle)', marginBottom: 16 }} />

          {/* Plans */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {PLANS.map(plan => {
              const isSelected = plan.id === selectedId;
              return (
                <button key={plan.id} type="button"
                  onClick={() => setSelectedId(plan.id)}
                  disabled={busy}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: 14,
                    border: isSelected
                      ? '2px solid var(--brand)'
                      : '2px solid var(--border-default)',
                    background: isSelected ? 'oklch(from var(--brand) l c h / 10%)' : 'var(--bg-card)',
                    cursor: busy ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 8,
                    transition: 'border-color 120ms, background 120ms',
                    fontFamily: FONT,
                    position: 'relative',
                  }}>
                  {/* Radio dot */}
                  <div style={{
                    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                    border: isSelected ? '5px solid var(--brand)' : '2px solid var(--border-default)',
                    background: 'transparent',
                    transition: 'border 120ms',
                  }} />

                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-1)' }}>
                      {plan.family ? '👨‍👩‍👧 ' : ''}{t(plan.labelKey)}
                    </div>
                    {plan.subKey && (
                      <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 1 }}>
                        {t(plan.subKey)}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    {plan.best && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        background: 'var(--brand)', color: '#fff',
                        padding: '2px 8px', borderRadius: 999,
                      }}>{t('paywall.best')}</span>
                    )}
                    <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--fg-1)' }}>
                      {plan.price}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {error && (
            <p style={{ fontSize: 12, color: 'var(--status-expired)', textAlign: 'center', marginBottom: 8, fontFamily: FONT }}>
              {error}
            </p>
          )}

          {/* Buy button */}
          <button type="button" onClick={handlePurchase} disabled={busy} style={{
            width: '100%', padding: '15px', borderRadius: 14, border: 'none',
            background: 'var(--brand)', color: '#fff',
            fontSize: 16, fontWeight: 800, fontFamily: FONT,
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.7 : 1,
            marginBottom: 8,
            transition: 'opacity 150ms',
          }}>
            {loading === 'buy' ? '…' : t('paywall.startWith', { price: selectedPlan.price })}
          </button>

          <button type="button" onClick={handleRestore} disabled={busy} style={{
            width: '100%', padding: '10px', background: 'none', border: 'none',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: 13, color: 'var(--fg-3)', fontFamily: FONT,
          }}>
            {loading === 'restore' ? '…' : t('paywall.restore')}
          </button>

          <button type="button" onClick={onClose} disabled={busy} style={{
            width: '100%', padding: '8px', background: 'none', border: 'none',
            cursor: 'pointer', fontSize: 12, color: 'var(--fg-4)', fontFamily: FONT,
          }}>
            {t('premium.notNow')}
          </button>

          <p style={{ fontSize: 11, color: 'var(--fg-4)', textAlign: 'center', margin: '8px 0 0', fontFamily: FONT }}>
            <a href="https://aihuset.dk/kitchey/privacy/" target="_blank" rel="noreferrer"
              style={{ color: 'var(--fg-4)' }}>{t('paywall.privacy')}</a>
            {' · '}
            <a href="https://aihuset.dk/kitchey/terms/" target="_blank" rel="noreferrer"
              style={{ color: 'var(--fg-4)' }}>{t('paywall.terms')}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
